import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const BREAKPOINTS = [
    { maxWidth: 576, slidesToShow: 1 },
    { maxWidth: 992, slidesToShow: 2 },
];

const resolveSlidesToShow = (base) => {
    if (typeof window === "undefined") return base;
    const width = window.innerWidth;
    const match = BREAKPOINTS.find((bp) => width <= bp.maxWidth);
    return Math.min(match ? match.slidesToShow : base, base);
};

const SWIPE_THRESHOLD = 40;

// A small, self-contained carousel replacing react-slick for this section.
//
// react-slick's rtl:true support only works correctly on a plain-LTR page:
// it mirrors slide order by reversing the DOM (see its track.js) and relies
// on that being the *only* mirroring in effect. This codebase always sets
// <html dir="rtl"> in Arabic (see App.jsx), which is an ancestor of every
// element regardless of react-slick forcing dir="ltr" on its own wrapper —
// so slick's base CSS (`[dir="rtl"] .slick-slide { float: right }`) also
// fires and stacks a *second* mirror on top of slick's own DOM reversal,
// canceling it out and making the carousel scroll like the English version.
// Rather than keep fighting slick's internals/CSS for one slider, this
// component owns its layout outright: it never uses a `[dir="rtl"]`
// selector, and only ever positions the track using this component's own
// `isRtl` flag.
const WonAuctionsCarousel = ({ children, className = "" }) => {
    const lang = useSelector((state) => state.translation.lang);
    const isRtl = lang === "ar";
    const items = React.Children.toArray(children);
    const count = items.length;
    const baseSlidesToShow = Math.max(1, Math.min(3, count || 3));

    const [slidesToShow, setSlidesToShow] = useState(() =>
        resolveSlidesToShow(baseSlidesToShow)
    );
    const [index, setIndex] = useState(0);
    const touchStartX = useRef(null);

    useEffect(() => {
        const handleResize = () =>
            setSlidesToShow(resolveSlidesToShow(baseSlidesToShow));
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [baseSlidesToShow]);

    const maxIndex = Math.max(0, count - slidesToShow);

    useEffect(() => {
        setIndex((current) => Math.min(current, maxIndex));
    }, [maxIndex]);

    if (!count) return null;

    const canPrev = index > 0;
    const canNext = index < maxIndex;
    const goPrev = () => setIndex((current) => Math.max(0, current - 1));
    const goNext = () => setIndex((current) => Math.min(maxIndex, current + 1));

    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(delta) < SWIPE_THRESHOLD) return;
        // Dragging left always visually shifts the track left, regardless
        // of language — only which semantic step (prev/next) that
        // corresponds to flips with direction (see the offset math below).
        const draggedLeft = delta < 0;
        if (isRtl ? draggedLeft : !draggedLeft) {
            goPrev();
        } else {
            goNext();
        }
    };

    // The DOM order is reversed for Arabic so the first item renders
    // physically rightmost, matching how the rest of the reading order
    // works. The track's own translateX math (in slide-count units) is
    // derived from that same physical layout: in LTR, index 0 sits at
    // track-start (offset 0) and each step shifts left (more negative).
    // In RTL, index 0 must show the rightmost `slidesToShow` items, i.e.
    // start already shifted left by (count - slidesToShow) slide-widths,
    // and each step shifts back toward 0 (revealing further-left/hidden
    // items entering from the left, per the requested behavior).
    const orderedItems = isRtl ? [...items].reverse() : items;
    const offsetUnits = isRtl ? index - maxIndex : -index;
    const trackOffsetPercent = offsetUnits * (100 / slidesToShow);

    return (
        <div className={`won-carousel ${className}`.trim()}>
            <div
                className="won-carousel__viewport"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
            >
                <div
                    className="won-carousel__track"
                    style={{
                        transform: `translateX(${trackOffsetPercent}%)`,
                    }}
                >
                    {orderedItems.map((child, i) => (
                        <div
                            key={child.key ?? i}
                            className="won-carousel__slide"
                            style={{ flexBasis: `${100 / slidesToShow}%` }}
                        >
                            {child}
                        </div>
                    ))}
                </div>
            </div>
            {maxIndex > 0 && (
                <>
                    <button
                        type="button"
                        className="won-carousel__arrow won-carousel__arrow--prev"
                        onClick={goPrev}
                        disabled={!canPrev}
                        aria-label="Previous"
                    />
                    <button
                        type="button"
                        className="won-carousel__arrow won-carousel__arrow--next"
                        onClick={goNext}
                        disabled={!canNext}
                        aria-label="Next"
                    />
                </>
            )}
        </div>
    );
};

export default WonAuctionsCarousel;
